/**
 * AdminVendors — vendor management inside the admin dashboard.
 *
 * Feature scope (Phase 1):
 *  - List vendors with a status filter (pending / active / suspended / all).
 *  - Approve pending vendors (status → active).
 *  - Suspend or reactivate any vendor.
 *  - Edit commission_rate inline.
 *  - View pending commissions per vendor + a global "Payouts" section
 *    listing every commission with a "Mark paid" action.
 *
 * Auth: piggybacks on the existing admin session cookie (`facetra_admin`)
 * — same pattern as AdminBillingLeaks, so `authHeader` is passed but
 * empty; cookies ride axios.defaults.withCredentials.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "@/lib/api";
import { toast } from "sonner";
import {
  Users, RefreshCw, CheckCircle2, XCircle, Loader2, IndianRupee,
  Copy, Percent, ChevronDown, ShieldOff, ShieldCheck, Sparkles,
} from "lucide-react";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import { getBackendUrl } from "@/lib/backend";
import { adminCreatePayout, adminAttributionRate, adminUpdatePayoutStatus, adminSetVendorUpi } from "@/lib/vendorApi";

const API = `${getBackendUrl()}/api`;

const STATUS_STYLE = {
  pending: "bg-amber-500/12 text-amber-300 border-amber-500/25",
  active: "bg-emerald-500/12 text-emerald-300 border-emerald-500/25",
  suspended: "bg-rose-500/12 text-rose-300 border-rose-500/25",
};

const STATUS_ORDER = ["", "pending", "active", "suspended"];
const STATUS_LABEL = {
  "": "All vendors",
  pending: "Pending approval",
  active: "Active",
  suspended: "Suspended",
};

export default function AdminVendors({ authHeader, onUnauthorized }) {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [editingRateFor, setEditingRateFor] = useState(null);
  const [rateInput, setRateInput] = useState("");

  // ---- Payouts sub-section state ----
  const [showPayouts, setShowPayouts] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState("pending");
  // Phase 2 — batch-payout modal state + attribution KPI
  const [payoutFor, setPayoutFor] = useState(null);
  const [payoutRef, setPayoutRef] = useState("");
  const [payoutNote, setPayoutNote] = useState("");
  const [payoutInitialStatus, setPayoutInitialStatus] = useState("sent");  // sent | pending
  const [payoutBusy, setPayoutBusy] = useState(false);
  const [attrRate, setAttrRate] = useState(null);
  const [modalPayouts, setModalPayouts] = useState([]);  // recent batched payouts for the vendor in the modal
  // iter 92 — inline UPI editing on vendor rows
  const [upiEditFor, setUpiEditFor] = useState(null);   // vendor_id being edited
  const [upiDraft, setUpiDraft] = useState("");
  const [upiBusy, setUpiBusy] = useState(false);

  // When the payout modal opens for a vendor, fetch their recent
  // batched-payouts so admin can see history + transition statuses.
  useEffect(() => {
    if (!payoutFor) { setModalPayouts([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/admin/vendors/${payoutFor.vendor_id}/payouts`,
          { params: { limit: 5 }, headers: authHeader },
        );
        if (!cancelled) setModalPayouts(data.items || []);
      } catch (_e) {
        if (!cancelled) setModalPayouts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [payoutFor, authHeader]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vendors`, {
        params: { status: statusFilter || undefined, limit: 200 },
        headers: authHeader,
      });
      setVendors(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error(err?.response?.data?.detail || "Could not load vendors.");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, authHeader, onUnauthorized]);

  const loadPayouts = useCallback(async () => {
    setPayoutsLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/vendors/commissions/all`, {
        params: { status: payoutStatus || undefined, limit: 200 },
        headers: authHeader,
      });
      setPayouts(data.items || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error(err?.response?.data?.detail || "Could not load payouts.");
    } finally {
      setPayoutsLoading(false);
    }
  }, [payoutStatus, authHeader, onUnauthorized]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (showPayouts) loadPayouts(); }, [showPayouts, loadPayouts]);

  // Phase 2 — attribution-rate KPI (small, fire once on mount).
  useEffect(() => {
    (async () => {
      try {
        const d = await adminAttributionRate({ rangeDays: 30 });
        setAttrRate(d);
      } catch (_e) {
        // Silent — KPI card just doesn't render if the endpoint fails.
      }
    })();
  }, []);

  // Phase 2 — batch-payout submit
  const submitBatchPayout = async () => {
    if (!payoutFor) return;
    setPayoutBusy(true);
    try {
      const res = await adminCreatePayout(payoutFor.vendor_id, {
        reference: payoutRef.trim() || null,
        note: payoutNote.trim() || null,
        initial_status: payoutInitialStatus,
      });
      if (res?.ok === false && res?.reason === "no_pending") {
        toast.message("No pending commissions", { description: "This vendor has nothing to disburse right now." });
      } else if (res?.ok && res?.payout) {
        const p = res.payout;
        const label = p.status === "pending" ? "queued as PENDING" : "recorded as SENT";
        toast.success(`Payout ₹${Number(p.total_amount).toFixed(0)} ${label} (${p.commission_count} commissions).`);
      }
      setPayoutFor(null);
      setPayoutRef("");
      setPayoutNote("");
      setPayoutInitialStatus("sent");
      await load();
      if (showPayouts) await loadPayouts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create payout.");
    } finally {
      setPayoutBusy(false);
    }
  };

  // iter 92 — inline UPI update handler
  const saveUpi = async (vendor_id) => {
    setUpiBusy(true);
    try {
      await adminSetVendorUpi(vendor_id, upiDraft.trim());
      toast.success("Vendor UPI updated.");
      setUpiEditFor(null);
      setUpiDraft("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid UPI.");
    } finally {
      setUpiBusy(false);
    }
  };

  // iter 92 — payout status transition handler (Sent → Failed / Refunded / …)
  const changePayoutStatus = async (payout, next_status) => {
    const askRef = (next_status === "sent" && !payout.reference);
    let ref = payout.reference;
    if (askRef) {
      ref = window.prompt("Enter the UPI/bank reference for this transfer:", payout.reference || "") || "";
      if (!ref.trim()) {
        toast.error("Reference required when marking as Sent.");
        return;
      }
    }
    try {
      await adminUpdatePayoutStatus(payout.vendor_id, payout.payout_id, {
        status: next_status,
        reference: askRef ? ref.trim() : null,
      });
      toast.success(`Payout marked ${next_status}.`);
      await loadPayouts();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not change payout status.");
    }
  };

  const copyUpi = (upi) => {
    if (!upi) return;
    navigator.clipboard.writeText(upi).then(
      () => toast.success(`Copied ${upi}`),
      () => toast.error("Copy failed — select and copy manually."),
    );
  };

  // ============ Actions ============
  const setStatus = async (vendorId, newStatus) => {
    try {
      await axios.post(
        `${API}/admin/vendors/${vendorId}/status`,
        { status: newStatus },
        { headers: authHeader },
      );
      toast.success(
        newStatus === "active" ? "Vendor approved."
        : newStatus === "suspended" ? "Vendor suspended."
        : "Vendor status updated.",
      );
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Status update failed.");
    }
  };

  const saveRate = async (vendorId) => {
    const rate = parseFloat(rateInput);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Rate must be between 0 and 100.");
      return;
    }
    try {
      await axios.post(
        `${API}/admin/vendors/${vendorId}/commission-rate`,
        { commission_rate: rate },
        { headers: authHeader },
      );
      toast.success(`Commission set to ${rate}%.`);
      setEditingRateFor(null);
      setRateInput("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update rate.");
    }
  };

  const markPaid = async (commissionId, reference) => {
    try {
      await axios.post(
        `${API}/admin/vendors/commissions/${commissionId}/mark-paid`,
        { reference: reference || null },
        { headers: authHeader },
      );
      toast.success("Commission marked as paid.");
      await loadPayouts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark paid.");
    }
  };

  const startMarkPaid = (commission) => {
    // Simple UPI/bank-ref input via prompt — admin action, low frequency,
    // avoids re-engineering the shared confirm modal.
    const ref = window.prompt(
      `Mark ₹${Number(commission.commission_amount).toFixed(0)} paid to ${commission.vendor?.business_name || "vendor"}.\n\n` +
      `Enter UPI ref / bank txn id (optional):`,
      "",
    );
    if (ref === null) return;  // cancelled
    markPaid(commission.commission_id, ref.trim());
  };

  const revertPaid = async (commissionId) => {
    try {
      await axios.post(
        `${API}/admin/vendors/commissions/${commissionId}/mark-pending`,
        {},
        { headers: authHeader },
      );
      toast.success("Reverted to pending.");
      await loadPayouts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not revert.");
    }
  };

  // ============ Filtered view ============
  const filtered = useMemo(() => vendors, [vendors]);

  return (
    <div className="space-y-6" data-testid="admin-vendors-tab">
      {/* Attribution KPI strip (Phase 2) */}
      {attrRate && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-attribution-kpis">
          <KpiCard label="Attribution rate (30d)" value={`${attrRate.attribution_rate_pct}%`} sub={`${attrRate.attributed} of ${attrRate.total_signups} signups`} />
          <KpiCard label="Attributed signups (30d)" value={attrRate.attributed} sub="via a valid vendor ref link" />
          <KpiCard label="Total new signups (30d)" value={attrRate.total_signups} sub="across all traffic sources" />
        </div>
      )}

      {/* Header row */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-[#141414] border border-white/8 rounded-xl p-1">
          {STATUS_ORDER.map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`admin-vendors-filter-${s || "all"}`}
              className={`text-xs px-3 py-1.5 rounded-lg transition ${
                statusFilter === s
                  ? "bg-[#D4AF37]/15 text-[#D4AF37]"
                  : "text-white/55 hover:text-white/85"
              }`}
            >
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
        <button
          onClick={load}
          data-testid="admin-vendors-refresh"
          className="ml-auto text-xs text-white/55 hover:text-white/85 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg hover:bg-white/5 transition"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
        <button
          onClick={() => setShowPayouts((v) => !v)}
          data-testid="admin-vendors-toggle-payouts"
          className="text-xs text-white/70 hover:text-white inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/6 hover:bg-white/10 transition"
        >
          <IndianRupee className="w-3.5 h-3.5" />
          {showPayouts ? "Hide payouts" : "Manage payouts"}
          <ChevronDown className={`w-3.5 h-3.5 transition ${showPayouts ? "rotate-180" : ""}`} />
        </button>
      </div>

      {/* Vendors table */}
      <div className="rounded-2xl border border-white/8 bg-black/40 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-white/50 text-[10px] uppercase tracking-widest border-b border-white/8">
              <th className="text-left px-4 py-3 font-normal">Business</th>
              <th className="text-left px-4 py-3 font-normal">Contact</th>
              <th className="text-left px-4 py-3 font-normal">Referral Code</th>
              <th className="text-right px-4 py-3 font-normal">Users</th>
              <th className="text-right px-4 py-3 font-normal">Pending ₹</th>
              <th className="text-right px-4 py-3 font-normal">Rate</th>
              <th className="text-left px-4 py-3 font-normal">Status</th>
              <th className="text-right px-4 py-3 font-normal">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center py-10 text-white/40"><Loader2 className="w-5 h-5 inline animate-spin" /></td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-10 text-white/40 text-xs">
                  No vendors {statusFilter && `with status "${statusFilter}"`}.
                </td>
              </tr>
            )}
            {!loading && filtered.map((v) => (
              <tr
                key={v.vendor_id}
                data-testid={`admin-vendor-row-${v.vendor_id}`}
                className="border-t border-white/6 hover:bg-white/[0.02]"
              >
                <td className="px-4 py-3">
                  <div className="text-white">{v.business_name}</div>
                  <div className="text-white/45 text-xs">{v.name}</div>
                </td>
                <td className="px-4 py-3 text-white/75">
                  <div>{v.email}</div>
                  {v.phone && <div className="text-white/40 text-xs">{v.phone}</div>}
                </td>
                <td className="px-4 py-3">
                  <div className="inline-flex items-center gap-1.5">
                    <code className="font-mono text-[#F3CE5A] text-xs bg-black/50 px-2 py-1 rounded">
                      {v.referral_code}
                    </code>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(v.referral_code);
                        toast.success("Code copied");
                      }}
                      className="text-white/40 hover:text-white p-1"
                      title="Copy code"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                </td>
                <td className="px-4 py-3 text-right text-white/75">{v.referred_user_count ?? 0}</td>
                <td className="px-4 py-3 text-right text-white/75">
                  {v.pending_commission > 0 ? (
                    <span className="text-amber-300">₹{Number(v.pending_commission).toFixed(0)}</span>
                  ) : (
                    <span className="text-white/30">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  {editingRateFor === v.vendor_id ? (
                    <div className="inline-flex items-center gap-1">
                      <input
                        type="number"
                        value={rateInput}
                        onChange={(e) => setRateInput(e.target.value)}
                        step="0.5"
                        min="0"
                        max="100"
                        data-testid={`admin-vendor-rate-input-${v.vendor_id}`}
                        className="w-16 bg-black/60 border border-[#D4AF37]/40 rounded px-2 py-1 text-sm text-white text-right"
                      />
                      <span className="text-white/50 text-xs">%</span>
                      <button
                        onClick={() => saveRate(v.vendor_id)}
                        data-testid={`admin-vendor-rate-save-${v.vendor_id}`}
                        className="text-emerald-300 hover:text-emerald-200 p-1"
                        title="Save"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => { setEditingRateFor(null); setRateInput(""); }}
                        className="text-white/40 hover:text-white p-1"
                        title="Cancel"
                      >
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => { setEditingRateFor(v.vendor_id); setRateInput(String(v.commission_rate ?? 20)); }}
                      data-testid={`admin-vendor-rate-edit-${v.vendor_id}`}
                      className="text-white/75 hover:text-[#D4AF37] inline-flex items-center gap-1 transition"
                      title="Click to edit"
                    >
                      {v.commission_rate ?? 20}<Percent className="w-3 h-3 opacity-60" />
                    </button>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[v.status] || ""}`}>
                    {v.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {v.status === "pending" && (
                      <button
                        onClick={() => setConfirm({ vendorId: v.vendor_id, newStatus: "active", business: v.business_name })}
                        data-testid={`admin-vendor-approve-${v.vendor_id}`}
                        className="text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/25 rounded-md px-2.5 py-1 inline-flex items-center gap-1 transition"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" /> Approve
                      </button>
                    )}
                    {v.status === "active" && (
                      <button
                        onClick={() => setConfirm({ vendorId: v.vendor_id, newStatus: "suspended", business: v.business_name })}
                        data-testid={`admin-vendor-suspend-${v.vendor_id}`}
                        className="text-xs bg-rose-500/12 hover:bg-rose-500/22 text-rose-300 border border-rose-500/25 rounded-md px-2.5 py-1 inline-flex items-center gap-1 transition"
                      >
                        <ShieldOff className="w-3.5 h-3.5" /> Suspend
                      </button>
                    )}
                    {v.status === "suspended" && (
                      <button
                        onClick={() => setConfirm({ vendorId: v.vendor_id, newStatus: "active", business: v.business_name })}
                        data-testid={`admin-vendor-reactivate-${v.vendor_id}`}
                        className="text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/25 rounded-md px-2.5 py-1 inline-flex items-center gap-1 transition"
                      >
                        <ShieldCheck className="w-3.5 h-3.5" /> Reactivate
                      </button>
                    )}
                    {v.status === "active" && (v.pending_commission || 0) > 0 && (
                      <button
                        onClick={() => setPayoutFor(v)}
                        data-testid={`admin-vendor-payout-${v.vendor_id}`}
                        className="text-xs bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25 text-[#D4AF37] border border-[#D4AF37]/30 rounded-md px-2.5 py-1 inline-flex items-center gap-1 transition"
                        title={`Batch-payout ₹${Number(v.pending_commission).toFixed(0)} of pending commissions`}
                      >
                        <IndianRupee className="w-3.5 h-3.5" /> Payout
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payouts drawer */}
      {showPayouts && (
        <div className="rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#D4AF37]/6 via-transparent to-transparent p-5 space-y-4" data-testid="admin-vendors-payouts">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h3 className="text-white font-display text-base">Commission payouts</h3>
              <p className="text-xs text-white/50 mt-0.5">Mark payouts as paid after transferring UPI / bank funds offline.</p>
            </div>
            <div className="flex items-center gap-1 bg-black/40 border border-white/8 rounded-lg p-1">
              {["pending", "paid", ""].map((s) => (
                <button
                  key={s || "all"}
                  onClick={() => setPayoutStatus(s)}
                  data-testid={`admin-payouts-filter-${s || "all"}`}
                  className={`text-xs px-3 py-1 rounded ${
                    payoutStatus === s ? "bg-white/8 text-white" : "text-white/50 hover:text-white/80"
                  }`}
                >
                  {s === "" ? "All" : s === "pending" ? "Pending" : "Paid"}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-white/8 bg-black/40 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/50 text-[10px] uppercase tracking-widest border-b border-white/8">
                  <th className="text-left px-4 py-3 font-normal">Vendor</th>
                  <th className="text-left px-4 py-3 font-normal">Customer</th>
                  <th className="text-left px-4 py-3 font-normal">Order</th>
                  <th className="text-right px-4 py-3 font-normal">₹ Order</th>
                  <th className="text-right px-4 py-3 font-normal">₹ Commission</th>
                  <th className="text-left px-4 py-3 font-normal">Status</th>
                  <th className="text-right px-4 py-3 font-normal">Action</th>
                </tr>
              </thead>
              <tbody>
                {payoutsLoading && (
                  <tr><td colSpan={7} className="text-center py-8 text-white/40"><Loader2 className="w-4 h-4 inline animate-spin" /></td></tr>
                )}
                {!payoutsLoading && payouts.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-white/40 text-xs">No commissions {payoutStatus && `(${payoutStatus})`}.</td></tr>
                )}
                {!payoutsLoading && payouts.map((c) => (
                  <tr key={c.commission_id} data-testid={`admin-payout-row-${c.commission_id}`} className="border-t border-white/6">
                    <td className="px-4 py-3">
                      <div className="text-white">{c.vendor?.business_name || "—"}</div>
                      <div className="text-white/45 text-xs">{c.vendor?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/75">
                      <div>{c.customer?.name || "—"}</div>
                      <div className="text-white/40 text-xs">{c.customer?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-white/60 font-mono text-xs">{c.order_id}</td>
                    <td className="px-4 py-3 text-right text-white/75">₹{Number(c.order_amount).toFixed(0)}</td>
                    <td className="px-4 py-3 text-right font-medium text-[#F3CE5A]">₹{Number(c.commission_amount).toFixed(0)}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${c.status === "paid" ? "bg-emerald-500/12 text-emerald-300" : "bg-amber-500/12 text-amber-300"}`}>
                        {c.status}
                      </span>
                      {c.status === "paid" && c.paid_reference && (
                        <div className="text-[10px] text-white/40 mt-1">ref: {c.paid_reference}</div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {c.status === "pending" ? (
                        <button
                          onClick={() => startMarkPaid(c)}
                          data-testid={`admin-payout-mark-paid-${c.commission_id}`}
                          className="text-xs bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/25 rounded-md px-2.5 py-1 inline-flex items-center gap-1 transition"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" /> Mark paid
                        </button>
                      ) : (
                        <button
                          onClick={() => revertPaid(c.commission_id)}
                          data-testid={`admin-payout-revert-${c.commission_id}`}
                          className="text-xs text-white/50 hover:text-white/85 transition"
                        >
                          Revert
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Status change confirm modal */}
      {confirm && (
        <LuxuryConfirmModal
          open
          onOpenChange={(open) => { if (!open) setConfirm(null); }}
          title={
            confirm.newStatus === "active" ? "Approve this vendor?"
            : confirm.newStatus === "suspended" ? "Suspend this vendor?"
            : "Change vendor status?"
          }
          description={
            confirm.newStatus === "active"
              ? `Activating ${confirm.business} — they'll be able to log in and start earning commission on referrals.`
              : `Suspending ${confirm.business} — all their active sessions will be revoked immediately. New referrals will stop paying commission.`
          }
          confirmLabel={confirm.newStatus === "active" ? "Approve" : "Suspend"}
          destructive={confirm.newStatus === "suspended"}
          onConfirm={async () => { await setStatus(confirm.vendorId, confirm.newStatus); setConfirm(null); }}
        />
      )}

      {/* Batch-payout modal (Phase 2) */}
      {payoutFor && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="admin-payout-modal"
          onClick={() => !payoutBusy && setPayoutFor(null)}
        >
          <div
            className="bg-[#0E0E0E] border border-[#D4AF37]/30 rounded-2xl w-full max-w-md p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-2 inline-flex items-center gap-1.5">
              <IndianRupee className="w-3 h-3" /> Batch payout
            </div>
            <h3 className="font-display text-xl text-white">
              Disburse ₹{Number(payoutFor.pending_commission).toFixed(0)} to {payoutFor.business_name}
            </h3>
            <p className="text-xs text-white/50 mt-1 leading-relaxed">
              This groups every pending commission for this vendor into a single payout record
              and marks each as paid. Enter the UPI / bank reference so the vendor can reconcile.
            </p>

            {/* iter 92 — vendor UPI with Copy button */}
            {payoutFor.upi_id ? (
              <div
                className="mt-4 rounded-xl border border-[#D4AF37]/25 bg-[#D4AF37]/[0.05] px-3.5 py-3 flex items-center gap-2.5"
                data-testid="admin-payout-vendor-upi"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]/85">Vendor UPI</div>
                  <div className="font-mono text-sm text-white truncate mt-0.5">{payoutFor.upi_id}</div>
                </div>
                <button
                  type="button"
                  onClick={() => copyUpi(payoutFor.upi_id)}
                  data-testid="admin-payout-copy-upi"
                  className="text-xs inline-flex items-center gap-1.5 bg-[#D4AF37]/15 hover:bg-[#D4AF37]/25 text-[#D4AF37] border border-[#D4AF37]/25 rounded-lg px-3 py-2 transition"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy
                </button>
              </div>
            ) : (
              <div className="mt-4 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-3.5 py-3 text-xs text-amber-200/90">
                No UPI on file for this vendor. Ask them to add it from the vendor dashboard, or set it on the row before sending money.
              </div>
            )}

            <div className="mt-4 space-y-3">
              {/* iter 92 — initial status toggle */}
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1.5">Initial status</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPayoutInitialStatus("sent")}
                    data-testid="admin-payout-status-sent"
                    className={`flex-1 rounded-lg text-xs px-3 py-2 border transition ${
                      payoutInitialStatus === "sent"
                        ? "bg-[#D4AF37]/20 border-[#D4AF37]/50 text-[#D4AF37]"
                        : "bg-[#141414] border-white/10 text-white/60 hover:border-white/20"
                    }`}
                  >
                    Sent (money transferred)
                  </button>
                  <button
                    type="button"
                    onClick={() => setPayoutInitialStatus("pending")}
                    data-testid="admin-payout-status-pending"
                    className={`flex-1 rounded-lg text-xs px-3 py-2 border transition ${
                      payoutInitialStatus === "pending"
                        ? "bg-white/12 border-white/40 text-white"
                        : "bg-[#141414] border-white/10 text-white/60 hover:border-white/20"
                    }`}
                  >
                    Pending (send later)
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">
                  Reference (UPI / bank txn) {payoutInitialStatus === "sent" && <span className="text-[#D4AF37]">*</span>}
                </label>
                <input
                  type="text"
                  value={payoutRef}
                  onChange={(e) => setPayoutRef(e.target.value)}
                  placeholder="UPI/2026-02-08/AXIS0002139"
                  data-testid="admin-payout-reference"
                  className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Note (optional, visible to vendor)</label>
                <textarea
                  value={payoutNote}
                  onChange={(e) => setPayoutNote(e.target.value)}
                  rows={2}
                  placeholder="Feb weekly disbursement"
                  data-testid="admin-payout-note"
                  className="w-full bg-[#141414] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#D4AF37]/60 resize-none"
                />
              </div>
            </div>

            {/* iter 92 — recent batched payouts + status transitions */}
            {modalPayouts.length > 0 && (
              <div className="mt-5 border-t border-white/8 pt-4" data-testid="admin-payout-history">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-2">Recent payouts for this vendor</div>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {modalPayouts.map((p) => (
                    <div key={p.payout_id} className="flex items-center gap-2 text-xs bg-white/[0.02] border border-white/6 rounded-lg px-3 py-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-white/70 text-[11px]">{p.payout_id}</span>
                          <span className="text-white/40">·</span>
                          <span className="text-white/60">₹{Number(p.total_amount).toFixed(0)}</span>
                          <span className="text-white/40">·</span>
                          <span className="text-white/40 text-[10px]">{new Date(p.created_at).toLocaleDateString()}</span>
                        </div>
                        {p.reference && <div className="text-white/40 text-[10px] font-mono mt-0.5 truncate">ref: {p.reference}</div>}
                      </div>
                      <select
                        value={p.status || "sent"}
                        onChange={(e) => changePayoutStatus(p, e.target.value)}
                        data-testid={`admin-payout-status-select-${p.payout_id}`}
                        className="bg-[#141414] border border-white/12 rounded text-[11px] text-white px-2 py-1 focus:outline-none focus:border-[#D4AF37]/60"
                      >
                        <option value="sent">Sent</option>
                        <option value="pending">Pending</option>
                        <option value="failed">Failed</option>
                        <option value="refunded">Refunded</option>
                      </select>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-white/35 mt-2 leading-relaxed">
                  Change status to <strong className="text-rose-300">Failed</strong> or <strong className="text-amber-300">Refunded</strong> to un-link commissions — they&apos;ll return to Pending and be included in the next payout.
                </p>
              </div>
            )}
            <div className="mt-5 flex items-center justify-end gap-2">
              <button
                type="button"
                disabled={payoutBusy}
                onClick={() => setPayoutFor(null)}
                className="px-4 py-2 text-sm text-white/60 hover:text-white/90 disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={payoutBusy}
                onClick={submitBatchPayout}
                data-testid="admin-payout-submit"
                className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2 hover:bg-[#F3CE5A] transition disabled:opacity-50"
              >
                {payoutBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Confirm payout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function KpiCard({ label, value, sub }) {
  return (
    <div className="rounded-2xl border border-white/8 bg-white/[0.02] p-4">
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">{label}</div>
      <div className="mt-1 font-display text-2xl text-white">{value}</div>
      {sub && <div className="text-[11px] text-white/40 mt-0.5">{sub}</div>}
    </div>
  );
}
